import * as React from 'react';

import { cn } from '../lib/cn';

export interface BottomTabItem {
  id: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  to?: string;
  badge?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
}

export interface BottomTabBarProps extends React.HTMLAttributes<HTMLDivElement> {
  items: BottomTabItem[];
  activeId?: string;
}

export const BottomTabBar = React.forwardRef<HTMLDivElement, BottomTabBarProps>(
  ({ className, items, activeId, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="tablist"
        aria-label="Primary"
        className={cn(
          // The safe-area inset must ADD to the bar's height, not eat into it.
          // With a fixed `h-16` the bottom padding was subtracted from the tabs
          // themselves, so on a phone with a home indicator every tab lost ~34px
          // of its touch height and the labels crowded the icons.
          'flex min-h-16 w-full items-stretch md:hidden',
          'h-[calc(4rem+env(safe-area-inset-bottom))]',
          'pb-[env(safe-area-inset-bottom)]',
          className,
        )}
        {...props}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          const baseClass = cn(
            'group relative flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5',
            'text-[10px] font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
            active
              ? 'text-[var(--color-accent)]'
              : 'text-[var(--color-fg-secondary)]',
          );

          const content = (
            <>
              {active ? (
                <span
                  aria-hidden
                  className="absolute top-0 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-b bg-[var(--color-accent)]"
                />
              ) : null}
              <span className="relative inline-flex h-5 w-5 items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
                {item.icon}
                {item.badge !== undefined && item.badge !== null ? (
                  <span className="absolute -right-1.5 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[9px] font-semibold text-white">
                    {item.badge}
                  </span>
                ) : null}
              </span>
              <span className="truncate">{item.label}</span>
            </>
          );

          if (item.to) {
            return (
              <a
                key={item.id}
                href={item.to}
                onClick={item.onClick as React.MouseEventHandler<HTMLAnchorElement>}
                className={baseClass}
                data-pop
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
              >
                {content}
              </a>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick as React.MouseEventHandler<HTMLButtonElement>}
              className={baseClass}
              role="tab"
              aria-selected={active}
            >
              {content}
            </button>
          );
        })}
      </div>
    );
  },
);
BottomTabBar.displayName = 'BottomTabBar';
